import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Instagram, Mail, Phone, ArrowUpRight, BookOpen, ChevronDown } from 'lucide-react';
import SlothLogo, { Wordmark } from './SlothLogo';
import WhatsAppFAB from './WhatsAppFAB';
import AmbientPlayer from './AmbientPlayer';
import CookieBanner from './CookieBanner';
import { useBlogLang } from '../hooks/useBlogLang';
import { useLenis } from '../hooks/useLenis';
import { CONTENT, INSTAGRAM_URL } from '../data/lempoleData';

/**
 * Shared shell for the blog pages: cream "paper" navbar (differentiated from
 * the terracotta ribbon used on the main site) and the same dark footer used
 * elsewhere. Ships two contextual navigation pills — a Diario shortcut and a
 * link back to the main site — so readers can move around without ever
 * feeling lost between the two properties.
 */
export default function BlogLayout({ children }) {
  const [scrolled, setScrolled] = useState(true);
  const { lang, setLang } = useBlogLang();
  const t = CONTENT[lang] || CONTENT.it;
  const suffix = lang === 'it' ? '' : `?lang=${lang}`;
  const location = useLocation();
  const isOnIndex = location.pathname === '/diario' || location.pathname === '/diario/';
  useLenis({ lerp: 0.08 });

  const labels = {
    it: { diario: 'Diario', sito: 'Home' },
    en: { diario: 'Diary', sito: 'Home' },
    fr: { diario: 'Journal', sito: 'Home' },
  }[lang] || { diario: 'Diario', sito: 'Home' };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="App font-body blog-shell">

      <nav
        className="blog-nav"
        style={{
          boxShadow: scrolled ? '0 1px 22px rgba(58, 36, 22, 0.09)' : 'none',
        }}
        data-testid="blog-nav"
      >
        <div className="blog-nav__brand-group">
          {/* Sloth + wordmark → main site. On the diary these were previously
              collapsed into a single Link but the vigneron asked for two clear
              targets: brand → main site, section tag → diary index. */}
          <Link to={`/${suffix}`} className="blog-nav__brand" data-testid="blog-nav-to-home">
            <span className="inline-flex"><SlothLogo size={52} /></span>
            <Wordmark height={38} className="drop-shadow-[0_1px_6px_rgba(0,0,0,0.35)]" />
          </Link>
          <Link
            to={`/diario${suffix}`}
            className="blog-nav__sectiontag font-display"
            data-testid="blog-nav-section"
          >
            <span className="blog-nav__sectiontag-sep" aria-hidden="true" />
            Il Diario
          </Link>
        </div>

        <div className="blog-nav__actions blog-nav__actions--desktop">
          {!isOnIndex && (
            <Link
              to={`/diario${suffix}`}
              className="blog-nav__pill"
              data-testid="blog-nav-to-diario"
            >
              <BookOpen size={14} />
              <span>{labels.diario}</span>
            </Link>
          )}
          <Link
            to={`/${suffix}`}
            className="blog-nav__pill blog-nav__pill--primary"
            data-testid="blog-nav-to-site"
          >
            <span>{labels.sito}</span>
            <ArrowUpRight size={14} />
          </Link>

          {/* Language selector — same minimal text-only version as the main site */}
          <div className="nav-lang nav-lang--diario" data-testid="blog-nav-lang">
            <button
              type="button"
              className="nav-lang__current"
              aria-haspopup="menu"
              aria-label={lang === 'it' ? 'Cambia lingua' : lang === 'fr' ? 'Changer de langue' : 'Change language'}
              data-testid="blog-nav-lang-current"
            >
              <span>{lang.toUpperCase()}</span>
              <ChevronDown size={12} strokeWidth={2.5} />
            </button>
            <div
              className="nav-lang__panel"
              role="menu"
              aria-label={lang === 'it' ? 'Seleziona lingua' : lang === 'fr' ? 'Choisir la langue' : 'Select language'}
            >
              {['it', 'en', 'fr'].filter((l) => l !== lang).map((l) => (
                <button
                  key={l}
                  type="button"
                  role="menuitem"
                  className="nav-lang__opt"
                  onClick={() => setLang(l)}
                  data-testid={`blog-nav-lang-${l}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <main>{children}</main>

      <footer className="pt-24 pb-10 px-6 md:px-16" style={{ background: '#1a1512', color: '#f2e9d8' }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-14 items-start">
          <div>
            <div className="flex items-center gap-1">
              <Wordmark height={56} />
            </div>
            <p className="font-display-italic mt-6 text-[1.1rem] opacity-90">{t.footer.tagline}</p>
          </div>

          <div className="font-body text-[0.95rem] leading-[1.9]">
            <p className="opacity-90">{t.footer.copyright}</p>
            <p className="opacity-90">{t.footer.address}</p>
            <p className="opacity-90">{t.footer.vat}</p>
            <p className="opacity-80 mt-3 text-sm">{t.footer.rights}</p>
            <div className="footer-legal-links mt-3" data-testid="blog-footer-legal-links">
              <a
                href="https://www.iubenda.com/privacy-policy/50690964"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-legal-link"
                data-testid="blog-footer-privacy-link"
              >{t.footer.privacy}</a>
              <span className="footer-legal-sep" aria-hidden="true">·</span>
              <a
                href="https://www.iubenda.com/privacy-policy/50690964/cookie-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-legal-link"
                data-testid="blog-footer-cookie-link"
              >{t.footer.cookie}</a>
              <span className="footer-legal-sep" aria-hidden="true">·</span>
              <Link
                to={`/termini${suffix}`}
                className="footer-legal-link"
                data-testid="blog-footer-terms-link"
              >{t.footer.terms}</Link>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-4">
              <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Instagram size={18} />
              </a>
              <a href={`mailto:${t.footer.email}`} aria-label="Email" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Mail size={18} />
              </a>
              <a href={`tel:${t.footer.phone.replace(/\s/g,'')}`} aria-label="Phone" className="w-11 h-11 rounded-full border border-current flex items-center justify-center hover:bg-white/10 transition-all duration-500">
                <Phone size={18} />
              </a>
            </div>
            <a href={`mailto:${t.footer.email}`} className="block mt-6 text-sm opacity-90 hover:opacity-100 font-body">{t.footer.email}</a>
            <a href={`tel:${t.footer.phone.replace(/\s/g,'')}`} className="block mt-1 text-sm opacity-90 hover:opacity-100 font-body">{t.footer.phone}</a>
            <p className="mt-6 text-sm opacity-90 font-body">{t.footer.handle}</p>

            <div className="flex items-center gap-3 mt-8" data-testid="blog-footer-lang-switcher">
              <button onClick={() => setLang('it')} className={`lang-btn ${lang === 'it' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>IT</button>
              <span className="opacity-50">|</span>
              <button onClick={() => setLang('en')} className={`lang-btn ${lang === 'en' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>EN</button>
              <span className="opacity-50">|</span>
              <button onClick={() => setLang('fr')} className={`lang-btn ${lang === 'fr' ? 'active' : ''}`} style={{ color: '#f2e9d8' }}>FR</button>
            </div>
          </div>
        </div>

        <div className="soft-divider mt-14" style={{ background: 'linear-gradient(90deg, transparent, rgba(242,233,216,0.3), transparent)' }} />

        {/* Legal / responsible drinking notice */}
        <div className="footer-disclaimer" data-testid="blog-footer-disclaimer">
          <span className="footer-disclaimer__badge" aria-label="18+">18<span className="footer-disclaimer__plus">+</span></span>
          <div className="footer-disclaimer__lines">
            <p className="footer-disclaimer__line font-display-italic">
              <svg className="footer-disclaimer__icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                <path d="M6 3 h12 l-1.4 8.4 A4.6 4.6 0 0 1 12 15.2 A4.6 4.6 0 0 1 7.4 11.4 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
                <path d="M12 15.2 V21" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                <path d="M9 21 H15" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
              </svg>
              {t.footer.disclaimer1}
            </p>
            <p className="footer-disclaimer__line footer-disclaimer__line--sub">
              {t.footer.disclaimer2}
              <span className="footer-disclaimer__age">{t.footer.ageWarn}</span>
            </p>
          </div>
        </div>

        <p className="text-center font-kicker text-[0.65rem] opacity-70 mt-6">Lempole — Slow Wine Philosophy</p>
      </footer>

      <WhatsAppFAB />
      <AmbientPlayer />
      <CookieBanner />
    </div>
  );
}
